import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import {
	useAutomationTemplateIdQuery,
	useInfiniteBoardsQuery,
	useStartAutomationForContactMutation,
} from '../../../../../queries';
import {
	invalidateGetOpportunitiesByCompany,
	useGetOpportunitiesByCompany,
} from '../../../../../queries/Boards/useGetOpportunitiesByCompany';
import {
	invalidateGetOpportunitiesByContact,
	useGetOpportunitiesByContact,
} from '../../../../../queries/Boards/useGetOpportunitiesByContact';
import { baseStyleSheet } from '../../../../styles/styles';
import { AddBoardItemButton } from '../../../boards/AddBoardItemButton';
import { Button } from '../../../Button';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { Modal } from '../../../Modal';
import { DataboardsIcon } from '../../../svgs/icons/DataboardsIcon';
import { EditOpportunityModal } from '../EditOpportunity';
import { OpportunityCard } from '../OpportunityCard';
import { styleSheet } from './styles';

const _EntityOpportunities = ({
	className,
	entity,
	styles,
	isContactMode,
}: {
	className?: string;
	entity: Api.IContact | Api.ICompany;
	styles?: StyleDeclarationValue[];
	isContactMode: boolean;
}) => {
	const errorMessages = useErrorMessages();
	const [opportunityToAdd, setOpportunityToAdd] = React.useState<Api.IOpportunity>(null);

	// Automation-related state
	const [pendingAutomationTemplateId, setPendingAutomationTemplateId] = React.useState<string>(null);
	const [triggeringOpportunity, setTriggeringOpportunity] = React.useState<Api.IOpportunity>(null);

	const userSession = useUserSession();
	const boardsQuery = useInfiniteBoardsQuery({
		type: 'Opportunity',
		expand: 'Stage.Tag',
	});

	const boards = React.useMemo(() => {
		return boardsQuery.data?.pages.map(page => page.values).flat() ?? [];
	}, [boardsQuery.data]);

	// Derive stages that have automations directly from boards data.
	// Replaces the previous useEffect + TagViewModel.load() loop.
	const stagesWithAutomations = React.useMemo<Api.IAccountTag[]>(() => {
		return boards.flatMap(board =>
			(board.stages ?? [])
				.filter(
					(stage): stage is Api.IBoardStageReference & { tag: Api.IAccountTag } => !!stage.tag?.automationTemplateId
				)
				.map(stage => stage.tag)
		);
	}, [boards]);

	const opportunitiesByContactQuery = useGetOpportunitiesByContact({
		enabled: isContactMode,
		contactId: entity.id,
	});
	const opportunitiesByCompanyQuery = useGetOpportunitiesByCompany({
		enabled: !isContactMode,
		companyId: entity.id,
	});

	const getAutomationTemplateByIdQuery = useAutomationTemplateIdQuery({
		enabled: !!pendingAutomationTemplateId,
		id: pendingAutomationTemplateId,
	});

	const automationTemplate = getAutomationTemplateByIdQuery.data;

	const startAutomationForContactMutation = useStartAutomationForContactMutation({
		onError: (error: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
		},
		onSuccess: () => {
			setTriggeringOpportunity(null);
			setPendingAutomationTemplateId(null);
		},
	});

	const ignoreAutomation = () => {
		setTriggeringOpportunity(null);
		setPendingAutomationTemplateId(null);
	};

	const startAutomation = () => {
		if (!automationTemplate || !triggeringOpportunity) return;

		startAutomationForContactMutation.mutate({
			template: automationTemplate,
			contactId: triggeringOpportunity.primaryContact.id,
			start: true,
		});
	};

	const onAddItemClicked = () => {
		if (isContactMode) {
			const primaryContact = entity as Api.IContact;
			setOpportunityToAdd({
				primaryContact,
				name: primaryContact.firstName + ' ' + primaryContact.lastName,
			});
		} else {
			setOpportunityToAdd({
				company: entity,
				name: entity.companyName,
			});
		}
	};

	const onOpportunitySave = (opportunity: Api.IOpportunity) => {
		const wasCreating = !opportunityToAdd?.id;
		const originalStageId = opportunityToAdd?.boardStage?.id;
		const newStageId = opportunity.boardStage?.id;
		const stageChanged = originalStageId !== newStageId;

		setOpportunityToAdd(null);

		// Check for automations when creating new opportunities OR when editing and stage changed
		if ((wasCreating || stageChanged) && newStageId) {
			// Find the board that contains this stage
			const board = boards.find(b => (b.stages ?? []).some(s => s.id === newStageId));
			if (board) {
				const stageName = (board.stages ?? []).find(x => x.id === newStageId)?.name;
				const toStageName = `${board.name}: ${stageName}`;
				const pendingAutomationForStage = stagesWithAutomations.find(
					x => x.name === toStageName && !!x.automationTemplateId
				);

				if (pendingAutomationForStage?.automationTemplateId) {
					setTriggeringOpportunity(opportunity);
					setPendingAutomationTemplateId(pendingAutomationForStage.automationTemplateId);
				}
			}
		}

		invalidateGetOpportunitiesByContact({ contactId: entity.id });
		if (opportunity.company?.id) {
			invalidateGetOpportunitiesByCompany({ companyId: opportunity.company.id });
		}
	};

	const opportunities = React.useMemo<Api.IOpportunity[]>(() => {
		if (isContactMode) return (opportunitiesByContactQuery.data?.values ?? []) as Api.IOpportunity[];
		return (opportunitiesByCompanyQuery.data?.values ?? []) as Api.IOpportunity[];
	}, [opportunitiesByContactQuery.data, opportunitiesByCompanyQuery.data, isContactMode]);

	return (
		<div className={`${css(...(styles || []))} ${className || ''}`}>
			<div className={css(styleSheet.contentSection)}>
				<header className={css(styleSheet.contentHeader)}>
					<figure className={css(styleSheet.boardIconContainer, baseStyleSheet.bgGreen)}>
						<DataboardsIcon height={16} width={21} className={css(styleSheet.opportuntitiesIcon)} />
					</figure>
					<h6 className={css(styleSheet.contentListTitle)}>Current Opportunities</h6>
					<AddBoardItemButton className={css(styleSheet.contentAddButton)} onClick={onAddItemClicked}>
						Add Opportunity
					</AddBoardItemButton>
				</header>
				<div className={css(styleSheet.cardList)}>
					{opportunities.length ? (
						<>
							{opportunities.map(x => {
								return (
									// TODO: Update this to use the new opportunity card
									<OpportunityCard
										className={css(styleSheet.item)}
										key={x.id}
										opportunity={x}
										showStageIndicator={true}
										onClickToEdit={() => setOpportunityToAdd(x)}
									/>
								);
							})}
						</>
					) : null}

					{!opportunitiesByContactQuery.isFetching &&
					!opportunitiesByCompanyQuery.isFetching &&
					!opportunities.length ? (
						<p className={css(styleSheet.emptyText)}>No opportunities exist for this contact.</p>
					) : null}

					{opportunityToAdd ? (
						<EditOpportunityModal
							opportunity={opportunityToAdd}
							onSave={onOpportunitySave}
							onCancel={() => setOpportunityToAdd(null)}
							boards={boards}
						/>
					) : null}

					{userSession?.account?.features?.automation?.enabled && (
						<Modal isOpen={!!pendingAutomationTemplateId} onRequestClose={ignoreAutomation} useDefaultHeader={true}>
							<div style={{ fontSize: 14, letterSpacing: '0', padding: '5px 10px' }}>
								<div style={{ color: '#4a4a4a', padding: '5px 12px' }}>This tag can trigger an automation flow:</div>
								{getAutomationTemplateByIdQuery.isSuccess ? (
									<>
										<div style={{ color: '#0073e6', padding: '5px 12px' }}>{`${automationTemplate?.name || ''}`}</div>
										<Button
											kind='primary'
											size='small'
											style={{ margin: '10px 10px', padding: '5px 12px' }}
											onClick={startAutomation}
										>
											<span>Start Automation</span>
										</Button>
									</>
								) : (
									<LoadingSpinner type='tiny' />
								)}
							</div>
						</Modal>
					)}
				</div>
			</div>
		</div>
	);
};

export const EntityOpportunities = observer(_EntityOpportunities);
